<!--
 * @Descripttion: 上传市场服务合同（向日葵服务助手专用）
 *   服务商自助上传线下签署的市场服务合同
 *   - 关联工业（必填，下拉仅当前服务商预绑定）
 *   - 合同名称（必填，≤50 字）
 *   - 线下签署日期（必填）
 *   - 服务商乙方（自动带出当前主体、只读）
 *   - 备注（选填，≤200 字）
 *   - 合同文件（必填，.pdf/.jpg/.png，≤10 MB）
 *   - 提交后写入列表并返回；若该工业要求「提交结算前须传合同」，上传完成后自动解除拦截
-->
<template>
  <div class="contract-market-upload">
    <BaseForm
      ref="formRef"
      :model="formData"
      :rules="rules"
      label-position="top"
      class="contract-market-upload__form"
      scroll-to-error
    >
      <!-- 基本信息 -->
      <div class="card">
        <div class="card__title">基本信息</div>
        <div class="card__grid">
          <BaseFormItem
            label="关联工业"
            prop="firstPartyId"
            required
            class="card__cell"
          >
            <BaseSelect
              placeholder="请选择关联工业（平台预绑定范围内）"
              labelKey="name"
              valueKey="id"
              init
              clearable
              filterUseRemote
              v-model="formData.firstPartyId"
              :remoteMethod="industryRemote"
            ></BaseSelect>
          </BaseFormItem>

          <BaseFormItem
            label="合同名称"
            prop="contractName"
            required
            class="card__cell"
          >
            <BaseInput
              v-model="formData.contractName"
              maxlength="50"
              show-word-limit
              placeholder="请输入合同名称（最多 50 字）"
              style="width: 100%"
            />
          </BaseFormItem>

          <BaseFormItem
            label="线下签署日期"
            prop="validity"
            required
            class="card__cell"
          >
            <BaseDatepickerLongterm
              style="width: 100%; margin-top: 1px"
              startPlaceholder="有效期始"
              endPlaceholder="有效期止"
              v-model:value="formData.validity"
              @update:value="handleValidityChange"
            />
          </BaseFormItem>

          <BaseFormItem label="服务商（乙方）" prop="" class="card__cell">
            <BaseInput :model-value="entpName" disabled style="width: 100%" />
          </BaseFormItem>

          <BaseFormItem
            label="备注 (选填)"
            prop="remarks"
            class="card__cell card__cell--full"
          >
            <BaseInput
              v-model="formData.remarks"
              type="textarea"
              :rows="4"
              maxlength="200"
              show-word-limit
              placeholder="可填写合同的补充说明，最多 200 字"
              style="width: 100%"
            />
          </BaseFormItem>
        </div>
      </div>

      <!-- 合同文件 -->
      <div class="card">
        <div class="card__title">合同文件</div>
        <BaseFormItem prop="contractRenderUrl" required>
          <BaseUpload
            v-xrk-loading="uploading"
            ref="uploadRef"
            v-model:fileList="formData.fileList"
            :auto-upload="true"
            :http-request="httpRequest"
            :accept="['.pdf', '.jpg', '.png']"
            :limit="1"
            :limitSize="10"
            :onError="onUploadError"
            style="width: 100%"
          >
            <template #default>
              <div class="upload-area">
                <BaseIcon size="48" color="#c1c7cd">
                  <Upload />
                </BaseIcon>
                <div class="upload-area__primary">
                  点击选择文件 或 拖拽合同扫描件到这里
                </div>
                <div class="upload-area__hint">
                  支持 .pdf / .jpg / .png，单文件不超过 10 MB
                </div>
              </div>
            </template>
          </BaseUpload>
        </BaseFormItem>
      </div>
    </BaseForm>

    <div class="affix-box">
      <BaseButton type="default" @click="onCancel">取消</BaseButton>
      <BaseButton style="margin-left: 12px" type="primary" :click="onSubmit">
        提交
      </BaseButton>
    </div>
  </div>
</template>

<script lang="tsx" setup>
import { computed, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  BaseForm,
  BaseFormItem,
  BaseInput,
  BaseSelect,
  BaseUpload,
  BaseUploadRef,
  BaseButton,
  BaseIcon,
  XrkMessage
} from 'xrk-components';
import { Upload } from '@element-plus/icons-vue';

import appStore from '@/store';
import { uploadApi } from '@/common/data-source/common/apis';
import {
  getBoundIndustryList,
  uploadMarketServiceContract
} from '../data-source/apis';

import { BaseDatepickerLongterm } from '@/components/base/base-datepicker-longterm/index';

const router = useRouter();
const route = useRoute();
const userStore = appStore.useUserStoreSetup;
const entpName = computed(() => userStore.info.entpName || '');

const { parentPath } = (route.meta as any) || {};
const tabQuery = (route.query.tab as string) || '';

const formRef = ref();
const uploadRef = ref<BaseUploadRef>(null);

const formData = reactive<{
  firstPartyId: number | null;
  contractName: string;
  validity: string;
  remarks: string;
  fileList: any[];
  contractRenderUrl: string;
  urlFileName: string;
}>({
  firstPartyId: null,
  contractName: '',
  validity: '',
  remarks: '',
  fileList: [],
  contractRenderUrl: '',
  urlFileName: ''
});

const rules = {
  firstPartyId: [
    { required: true, message: '请选择关联工业', trigger: 'change' }
  ],
  contractName: [
    { required: true, message: '请输入合同名称', trigger: 'blur' },
    { max: 50, message: '合同名称最多 50 字', trigger: 'blur' }
  ],
  validity: [
    { required: true, message: '请选择线下签署日期', trigger: 'change' }
  ],
  remarks: [{ max: 200, message: '备注最多 200 字', trigger: 'blur' }],
  contractRenderUrl: [
    { required: true, message: '请上传合同扫描件', trigger: 'change' }
  ]
};

const industryRemote = async entpName => {
  const resp = await getBoundIndustryList({
    type: 2, // 你写死 2-合同管理     类型 1 合同模板管理   2合同管理
    entpName,
    participantValue: 1, // 查询工业-1  查询服务商-2
    contractType: 3, // 合同类型 3-市场服务
    taxPattern: 0, // 交易结构 不用传
    source: 2, // 写死2  来源 ： 来源  1-模板过来的， 2-合同过来的
    pageNum: 1, //必传
    pageSize: 80 // 必传
  });
  return resp;
};

const handleValidityChange = () => {
  formRef.value.validateField('validity');
};

const uploading = ref(false);
const httpRequest = async ({ file }) => {
  uploading.value = true;
  return uploadApi({ file }).then(
    res => {
      formData.urlFileName = res.data.fileName;
      uploading.value = false;
      return res;
    },
    err => {
      uploading.value = false;
      return Promise.reject(err);
    }
  );
};

const onUploadError = (info: { msg: string }) => {
  XrkMessage.error(info.msg);
};

const goBackToList = () => {
  // 回来源页：parentPath + ?tab=<MARKET tab id>
  router.push({
    path: parentPath || '/main/asp/contract-mgmt/list',
    query: tabQuery ? { tab: tabQuery } : {}
  });
};

const onCancel = () => goBackToList();

const onSubmit = async () => {
  // 同步上传地址到 formData.contractRenderUrl 给规则校验
  if (formData.fileList?.length) {
    formData.contractRenderUrl = formData.fileList[0]?.url || '';
  } else {
    formData.contractRenderUrl = '';
  }

  try {
    await formRef.value.validate();
  } catch {
    XrkMessage.error('请补全标 * 的必填项');
    return;
  }

  await uploadMarketServiceContract({
    firstPartyId: formData.firstPartyId, // 签署方（关联工业）
    contractName: formData.contractName, // 合同名称
    validityStart: formData.validity?.[0], // 签署日期起
    validityEnd: formData.validity?.[1], // 签署日期止
    remarks: formData.remarks, // 备注
    contractRenderUrl: formData.contractRenderUrl, //上传文件
    urlFileName: formData.urlFileName, //上传文件
    type: 3
  });
  XrkMessage.success('提交成功');
  goBackToList();
};
</script>

<style lang="scss" scoped>
.contract-market-upload {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__form {
    flex: 1;
    overflow-y: auto;
  }

  .card {
    background: #fff;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 12px;
    border: 1px solid #e0e3e7;
    &__title {
      font-weight: 600;
      font-size: 16px;
      color: #121619;
      line-height: 24px;
      padding-left: 8px;
      border-left: 3px solid var(--xrk-color-primary, #f56c2d);
      margin-bottom: 16px;
    }
    &__grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px 24px;
    }
    &__cell--full {
      grid-column: 1 / -1;
    }
    &__hint {
      font-size: 12px;
      color: #898989;
      margin-top: 4px;
    }
  }

  .upload-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
    &__primary {
      font-size: 14px;
      color: #525252;
      margin-top: 8px;
    }
    &__hint {
      font-size: 12px;
      color: #898989;
      margin-top: 4px;
    }
  }

  .affix-box {
    position: sticky;
    bottom: 0;
    background: #fff;
    padding: 12px 20px;
    text-align: right;
    border-top: 1px solid #e0e3e7;
  }
}
</style>
